import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CreateInvestorModal from '../components/CreateInvestorModal';
import AlertDialog from '../components/AlertDialog';
import FileUpload from '../components/FileUpload';
import CreateNetworkUserModal from '../components/CreateNetworkUserModal';
import NetworkUserModal from '../components/NetworkUserModal';
import InvestmentTabbedDetails from '../components/investments/InvestmentTabbedDetails';
import { getApiUrl } from '../config/api.config';
import { investmentRoundAPI, networkUserAPI } from '../lib/api';

const InvestorNetworkDetail = () => {
    const { id, tenantId } = useParams();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [network, setNetwork] = useState(null);
    const [investors, setInvestors] = useState([]);
    const [startups, setStartups] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [events, setEvents] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'investors');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateNetworkUserModal, setShowCreateNetworkUserModal] = useState(false);
    const [showLinkStartupModal, setShowLinkStartupModal] = useState(false);
    const [showInvestmentDetailModal, setShowInvestmentDetailModal] = useState(false);
    const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [showExitModal, setShowExitModal] = useState(false);
    const [exitDetails, setExitDetails] = useState({
        exitDate: new Date().toISOString().split('T')[0],
        exitAmount: '',
        exitValuation: ''
    });
    const [allStartups, setAllStartups] = useState([]);
    const [selectedStartupId, setSelectedStartupId] = useState('');
    const [selectedInvestorIds, setSelectedInvestorIds] = useState([]);
    const [linkLoading, setLinkLoading] = useState(false);
    const [showAddExistingInvestorModal, setShowAddExistingInvestorModal] = useState(false);
    const [allInvestors, setAllInvestors] = useState([]);
    const [selectedExistingInvestorIds, setSelectedExistingInvestorIds] = useState([]);
    const [addExistingLoading, setAddExistingLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [investmentDetails, setInvestmentDetails] = useState({
        investmentAmount: '',
        currency: 'INR',
        investmentDate: new Date().toISOString().split('T')[0],
        equityPercentage: '',
        // Entry Options
        entrySharePrice: '',
        entryShareEquity: '',
        // Exit Options
        exitDate: '',
        exitValuation: '',
        investmentStage: 'SEED',
        investmentType: 'EQUITY',
        notes: ''
    });
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    // Investment Tranches state
    const [networkRounds, setNetworkRounds] = useState([]);
    const [selectedNetworkRound, setSelectedNetworkRound] = useState(null);

    // Network Users state
    const [networkUsers, setNetworkUsers] = useState([]);
    const [networkUsersLoading, setNetworkUsersLoading] = useState(false);
    const [showNetworkUserModal, setShowNetworkUserModal] = useState(false);
    const [selectedNetworkUser, setSelectedNetworkUser] = useState(null);
    const [isEditingNetworkUser, setIsEditingNetworkUser] = useState(false);
    const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
    const [userToRemove, setUserToRemove] = useState(null);
    const [editData, setEditData] = useState({
        organizationName: '',
        organizationType: '',
        email: '',
        phoneNumber: '',
        department: ''
    });

    // INVESTOR_NETWORK can only view their own network (when on investor-network/networks/:id)
    useEffect(() => {
        if (authUser?.role === 'INVESTOR_NETWORK' && id && authUser.id != null && String(id) !== String(authUser.id)) {
            const tenantPath = authUser.tenantIdentifier || tenantId || 'default';
            navigate(`/${tenantPath}/investor-network`, { replace: true });
            return;
        }
        // MANAGEMENT: can only view their assigned network
        if (authUser?.role === 'MANAGEMENT' && id && authUser.investorNetworkId != null && String(id) !== String(authUser.investorNetworkId)) {
            const tenantPath = authUser.tenantIdentifier || tenantId || 'default';
            navigate(`/${tenantPath}/investor-network/networks/${authUser.investorNetworkId}`, { replace: true });
        }
    }, [authUser, id, navigate, tenantId]);

    // Back destination: investor network users and MANAGEMENT go to dashboard; admins go to investors list
    const tenantPath = authUser?.tenantIdentifier || tenantId || 'default';
    const isNetworkUser = authUser?.role === 'INVESTOR_NETWORK';
    const isManagementUser = authUser?.role === 'MANAGEMENT';
    const isInvestorViewer = authUser?.role === 'INVESTOR';
    const canManageNetwork = authUser?.role === 'INVESTOR_NETWORK' || authUser?.role === 'ADMIN' || authUser?.role === 'TENANT_ADMIN';
    const backUrl = authUser?.role === 'INVESTOR'
        ? `/${tenantPath}/investor/networks`
        : (isNetworkUser || isManagementUser)
            ? `/${tenantPath}/investor-network`
            : `/${tenantId}/investors`;

    useEffect(() => {
        loadNetworkDetails();
    }, [id]);

    useEffect(() => {
        if (showLinkStartupModal) {
            loadAllStartups();
        }
    }, [showLinkStartupModal]);

    useEffect(() => {
        if (showAddExistingInvestorModal) {
            loadAllInvestors();
        }
    }, [showAddExistingInvestorModal]);

    // Update URL when tab changes
    useEffect(() => {
        setSearchParams({ tab: activeTab });
    }, [activeTab, setSearchParams]);

    // Sync activeTab when URL query param changes
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (!tabFromUrl) return;
        const normalizedTab = tabFromUrl === 'investment-rounds' ? 'investment-tranches' : tabFromUrl;
        if (['investors', 'startups', 'investment-tranches', 'events', 'files', 'users'].includes(normalizedTab)) {
            setActiveTab(normalizedTab);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!isInvestorViewer) return;
        if (!['investors', 'startups'].includes(activeTab)) {
            setActiveTab('investors');
            setSearchParams({ tab: 'investors' });
        }
    }, [isInvestorViewer, activeTab, setSearchParams]);

    // Load network users when tab is active
    useEffect(() => {
        if (activeTab === 'users' && id) {
            loadNetworkUsers();
        }
    }, [activeTab, id]);

    // Load investment tranches when tab is active
    useEffect(() => {
        if (activeTab === 'investment-tranches' && id) {
            loadNetworkRounds();
        }
    }, [activeTab, id]);

    const loadNetworkRounds = async () => {
        try {
            const rounds = await investmentRoundAPI.getNetworkRounds(id);
            setNetworkRounds(rounds || []);
        } catch (error) {
            console.error('Failed to load network investment rounds:', error);
            setNetworkRounds([]);
        }
    };

    const loadNetworkUsers = async () => {
        setNetworkUsersLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        try {
            if (authUser?.role === 'MANAGEMENT') {
                const res = await fetch(getApiUrl('users/my-network/users'), { headers });
                const users = res.ok ? await res.json() : [];
                setNetworkUsers(users || []);
            } else {
                const users = await networkUserAPI.getNetworkUsers(id);
                setNetworkUsers(users || []);
            }
        } catch (error) {
            console.error('Failed to load network users:', error);
            setNetworkUsers([]);
        } finally {
            setNetworkUsersLoading(false);
        }
    };

    const handleRemoveNetworkUser = async () => {
        if (!userToRemove) return;

        try {
            await networkUserAPI.removeNetworkUser(id, userToRemove.id);
            setAlert({
                isOpen: true,
                title: 'Success',
                message: `${userToRemove.name} has been removed from the network`,
                type: 'success'
            });
            setShowRemoveUserModal(false);
            setUserToRemove(null);
            await loadNetworkUsers();
        } catch (error) {
            console.error('Failed to remove network user:', error);
            setAlert({
                isOpen: true,
                title: 'Error',
                message: error.message || 'Failed to remove user from network',
                type: 'error'
            });
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'CEO':
                return 'bg-purple-100 text-purple-800';
            case 'ADMIN':
                return 'bg-blue-100 text-blue-800';
            case 'ACCOUNTANT':
                return 'bg-green-100 text-green-800';
            case 'MANAGEMENT':
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatMoney = (amount, currency = 'INR') => {
        if (amount == null || amount === '') return '—';
        const n = typeof amount === 'number' ? amount : Number(String(amount));
        if (Number.isNaN(n)) return '—';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency || 'INR',
            maximumFractionDigits: 2
        }).format(n);
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const getRoundStatusBadge = (status) => {
        const colors = {
            OPEN: 'bg-green-100 text-green-800',
            CLOSED: 'bg-gray-100 text-gray-800',
            CANCELLED: 'bg-red-100 text-red-800',
            COMMITTED: 'bg-blue-100 text-blue-800',
            PARTIAL: 'bg-yellow-100 text-yellow-800',
            COMPLETED: 'bg-green-100 text-green-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
            PAID: 'bg-green-100 text-green-800',
            OVERDUE: 'bg-red-100 text-red-800',
            ACTIVE: 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const loadNetworkDetails = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        try {
            // When viewer is an INVESTOR, use read-only endpoint (admin APIs return 403 for INVESTOR)
            if (authUser?.role === 'INVESTOR') {
                const res = await fetch(getApiUrl(`users/my-linked-network/${id}`), { headers });
                if (!res.ok) {
                    setNetwork(null);
                    setInvestors([]);
                    setLoading(false);
                    const tenantPath = authUser?.tenantIdentifier || tenantId || 'default';
                    navigate(`/${tenantPath}/investor/networks`, { replace: true });
                    return;
                }
                const data = await res.json();
                const net = data.network || null;
                const invList = data.investors || [];
                const investedStartups = data.investedStartups || [];
                setNetwork(net);
                setInvestors(invList);
                setStartups(Array.isArray(investedStartups) ? investedStartups : []);
                await Promise.all([
                    loadInvestments(invList),
                    loadRelatedEvents(invList),
                    loadRelatedFiles()
                ]);
                return;
            }

            // MANAGEMENT: use my-network APIs (no admin access)
            if (authUser?.role === 'MANAGEMENT') {
                const [profileRes, invRes] = await Promise.all([
                    fetch(getApiUrl('users/my-network/profile'), { headers }),
                    fetch(getApiUrl('users/my-network/investors'), { headers })
                ]);
                if (!profileRes.ok || !invRes.ok) {
                    setNetwork(null);
                    setInvestors([]);
                    setLoading(false);
                    return;
                }
                const profile = await profileRes.json();
                const invList = await invRes.json();
                if (id && profile && String(profile.id) !== String(id)) {
                    setNetwork(null);
                    setInvestors([]);
                    setLoading(false);
                    return;
                }
                setNetwork(profile);
                setInvestors(invList || []);
                await Promise.all([
                    loadRelatedStartups(invList || []),
                    loadInvestments(invList || []),
                    loadRelatedEvents(invList || []),
                    loadRelatedFiles()
                ]);
                setLoading(false);
                return;
            }

            // Admin or INVESTOR_NETWORK: use admin APIs
            const [networkResponse, investorsResponse] = await Promise.all([
                fetch(getApiUrl(`admin/users/${id}`), { headers }),
                fetch(getApiUrl(`admin/users/investors?networkId=${id}`), { headers })
            ]);

            if (!networkResponse.ok || !investorsResponse.ok) {
                setNetwork(null);
                setInvestors([]);
                return;
            }
            const textN = await networkResponse.text();
            const textI = await investorsResponse.text();
            let networkData = null;
            let investorsData = [];
            try {
                networkData = textN ? JSON.parse(textN) : null;
            } catch (_) { /* ignore */ }
            try {
                investorsData = textI ? JSON.parse(textI) : [];
            } catch (_) { /* ignore */ }

            setNetwork(networkData);
            setInvestors(investorsData || []);

            await Promise.all([
                loadRelatedStartups(investorsData || []),
                loadInvestments(investorsData || []),
                loadRelatedEvents(investorsData || []),
                loadRelatedFiles()
            ]);
        } catch (error) {
            console.error('Failed to load network details:', error);
            setNetwork(null);
            setInvestors([]);
        } finally {
            setLoading(false);
        }
    };

    const loadRelatedStartups = async (networkInvestors) => {
        try {
            // Get unique startups from all investments by investors in this network
            const allStartups = new Set();

            for (const investor of networkInvestors) {
                try {
                    const portfolioResponse = await fetch(getApiUrl(`investments/investor/${investor.id}/portfolio`), {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (portfolioResponse.ok) {
                        const portfolio = await portfolioResponse.json();
                        portfolio.forEach(startup => allStartups.add(JSON.stringify(startup)));
                    }
                } catch (err) {
                    console.error(`Failed to load portfolio for investor ${investor.id}:`, err);
                }
            }

            const uniqueStartups = Array.from(allStartups).map(s => JSON.parse(s));
            setStartups(uniqueStartups);
        } catch (error) {
            console.error('Failed to load related startups:', error);
            setStartups([]);
        }
    };

    const loadInvestments = async (networkInvestors) => {
        try {
            const allInvestments = [];

            for (const investor of networkInvestors) {
                try {
                    const investmentsResponse = await fetch(getApiUrl(`investments/investor/${investor.id}`), {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (investmentsResponse.ok) {
                        const investments = await investmentsResponse.json();
                        allInvestments.push(...investments);
                    }
                } catch (err) {
                    console.error(`Failed to load investments for investor ${investor.id}:`, err);
                }
            }

            setInvestments(allInvestments);
        } catch (error) {
            console.error('Failed to load investments:', error);
            setInvestments([]);
        }
    };

    const handleExitInvestment = async () => {
        if (!selectedInvestment) return;

        try {
            const exitData = {
                exitDate: exitDetails.exitDate ? new Date(exitDetails.exitDate).toISOString() : new Date().toISOString(),
                exitAmount: exitDetails.exitAmount ? parseFloat(exitDetails.exitAmount) : null,
                exitValuation: exitDetails.exitValuation ? parseFloat(exitDetails.exitValuation) : null
            };

            const response = await fetch(getApiUrl(`investments/${selectedInvestment.id}/exit`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(exitData)
            });

            if (response.ok) {
                setAlert({
                    isOpen: true,
                    title: 'Success',
                    message: 'Investment marked as exited successfully',
                    type: 'success'
                });
                setShowExitModal(false);
                setSelectedInvestment(null);
                setExitDetails({
                    exitDate: new Date().toISOString().split('T')[0],
                    exitAmount: '',
                    exitValuation: ''
                });
                // Reload investments
                await loadInvestments(investors);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to mark investment as exited');
            }
        } catch (error) {
            console.error('Failed to exit investment:', error);
            setAlert({
                isOpen: true,
                title: 'Error',
                message: error.message || 'Failed to mark investment as exited',
                type: 'error'
            });
        }
    };

    const loadRelatedEvents = async (networkInvestors) => {
        try {
            // Get all events and filter by events where network investors are organizers
            // or where participant startups have investments from network investors
            const allEvents = [];
            const investorIds = networkInvestors.map(inv => inv.id);

            const eventsResponse = await fetch(getApiUrl('pitch-events'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (eventsResponse.ok) {
                const eventsData = await eventsResponse.json();

                // Filter events where network investors are organizers
                for (const event of eventsData) {
                    // Check if any network investor is the organizer
                    if (event.organizer && investorIds.includes(event.organizer.id)) {
                        allEvents.push(event);
                        continue;
                    }

                    // Check if event has a startup field and that startup has investments from network investors
                    if (event.startup && event.startup.id) {
                        try {
                            const investorsResponse = await fetch(getApiUrl(`investments/startup/${event.startup.id}/investors`), {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            if (investorsResponse.ok) {
                                const startupInvestors = await investorsResponse.json();
                                const hasNetworkInvestor = startupInvestors.some(inv => investorIds.includes(inv.id));
                                if (hasNetworkInvestor) {
                                    allEvents.push(event);
                                }
                            }
                        } catch (err) {
                            console.error(`Failed to check investors for startup ${event.startup.id}:`, err);
                        }
                    }
                }
            }

            setEvents(allEvents);
        } catch (error) {
            console.error('Failed to load related events:', error);
            setEvents([]);
        }
    };

    const loadRelatedFiles = async () => {
        try {
            const filesResponse = await fetch(getApiUrl(`files?moduleType=INVESTOR_NETWORK&moduleId=${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (filesResponse.ok) {
                const filesData = await filesResponse.json();
                setFiles(filesData || []);
            } else if (filesResponse.status === 204) {
                setFiles([]);
            }
        } catch (error) {
            console.error('Failed to load related files:', error);
            setFiles([]);
        }
    };

    const loadSharedFiles = async (networkInvestors) => {
        try {
            // Get shared files accessible to investors in this network
            // We'll get files that are PUBLIC, NETWORK visibility, or have explicit permissions
            const allSharedFiles = [];
            const seenFileIds = new Set();
            const networkFileIds = new Set(files.map(f => f.id));
            const investorIds = new Set(networkInvestors.map(inv => inv.id));

            // Get all accessible files
            try {
                const allFilesResponse = await fetch(getApiUrl('files'), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });

                if (allFilesResponse.ok) {
                    const allFilesData = await allFilesResponse.json();

                    allFilesData.forEach(file => {
                        // Skip if already in network files
                        if (networkFileIds.has(file.id)) return;
                        if (seenFileIds.has(file.id)) return;

                        // Include files that are:
                        // 1. Public or Network visibility
                        // 2. Have permissions for network investors
                        // 3. Not uploaded by network investors (to avoid duplicates)
                        const isPublicOrNetwork = file.visibility === 'PUBLIC' || file.visibility === 'NETWORK';
                        const hasInvestorPermission = file.permissions?.some(p => investorIds.has(p.userId)) ||
                            file.sharedWith?.some(u => investorIds.has(u.id));
                        const notUploadedByInvestor = !investorIds.has(file.uploadedBy?.id);

                        if ((isPublicOrNetwork || hasInvestorPermission) && notUploadedByInvestor) {
                            // Find which investor(s) it's shared with
                            const sharedWithInvestors = networkInvestors.filter(inv =>
                                file.permissions?.some(p => p.userId === inv.id) ||
                                file.sharedWith?.some(u => u.id === inv.id)
                            );

                            const sharedWithText = sharedWithInvestors.length > 0
                                ? sharedWithInvestors.map(inv => inv.name || inv.email).join(', ')
                                : (isPublicOrNetwork ? 'All Network Investors' : 'Network Investors');

                            allSharedFiles.push({
                                ...file,
                                sharedWith: sharedWithText,
                                isShared: true
                            });
                            seenFileIds.add(file.id);
                        }
                    });
                }
            } catch (err) {
                console.error('Failed to load shared files:', err);
            }

            setSharedFiles(allSharedFiles);
        } catch (error) {
            console.error('Failed to load shared files:', error);
            setSharedFiles([]);
        }
    };

    const handleUploadSuccess = async (fileName) => {
        setAlert({ isOpen: true, title: 'Success', message: `File "${fileName}" uploaded successfully!`, type: 'success' });
        await loadRelatedFiles();
        setShowUploadModal(false);
    };

    const handleUploadError = (errorMessage) => {
        setAlert({ isOpen: true, title: 'Error', message: errorMessage || 'Failed to upload file', type: 'error' });
    };

    const loadAllStartups = async () => {
        try {
            const response = await fetch(getApiUrl('admin/users/startups'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAllStartups(data || []);
            }
        } catch (error) {
            console.error('Failed to load startups:', error);
            setAllStartups([]);
        }
    };

    const loadAllInvestors = async () => {
        try {
            const response = await fetch(getApiUrl('admin/users/investors'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAllInvestors(data || []);
            }
        } catch (error) {
            console.error('Failed to load investors:', error);
            setAllInvestors([]);
        }
    };

    const handleAddExistingInvestor = async () => {
        if (selectedExistingInvestorIds.length === 0) {
            setAlert({ isOpen: true, title: 'Error', message: 'Please select at least one investor', type: 'error' });
            return;
        }

        setAddExistingLoading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            // Process all selected investors
            const results = await Promise.all(
                selectedExistingInvestorIds.map(async (investorId) => {
                    try {
                        const response = await fetch(getApiUrl(`admin/users/${investorId}`), {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: JSON.stringify({ investorNetworkId: id })
                        });
                        return response.ok;
                    } catch (e) {
                        return false;
                    }
                })
            );

            successCount = results.filter(r => r).length;
            failCount = results.filter(r => !r).length;

            if (successCount > 0) {
                setAlert({
                    isOpen: true,
                    title: 'Success',
                    message: `Successfully added ${successCount} investor${successCount > 1 ? 's' : ''} to network.${failCount > 0 ? ` Failed to add ${failCount}.` : ''}`,
                    type: 'success'
                });
                setShowAddExistingInvestorModal(false);
                setSelectedExistingInvestorIds([]);
                await loadNetworkDetails();
            } else {
                setAlert({
                    isOpen: true,
                    title: 'Error',
                    message: 'Failed to add selected investors',
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('Failed to add investors:', error);
            setAlert({
                isOpen: true,
                title: 'Error',
                message: 'An unexpected error occurred',
                type: 'error'
            });
        } finally {
            setAddExistingLoading(false);
        }
    };

    const handleLinkStartup = async () => {
        if (!selectedStartupId) {
            setAlert({ isOpen: true, title: 'Error', message: 'Please select a startup', type: 'error' });
            return;
        }

        if (selectedInvestorIds.length === 0) {
            setAlert({ isOpen: true, title: 'Error', message: 'Please select at least one investor', type: 'error' });
            return;
        }

        if (!investmentDetails.investmentAmount || parseFloat(investmentDetails.investmentAmount) <= 0) {
            setAlert({ isOpen: true, title: 'Error', message: 'Please enter a valid investment amount', type: 'error' });
            return;
        }

        setLinkLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            // Create investments for each selected investor with the provided details
            const investmentPromises = selectedInvestorIds.map(investorId => {
                const investmentData = {
                    investorId: investorId,
                    startupId: selectedStartupId,
                    investmentDate: investmentDetails.investmentDate ? new Date(investmentDetails.investmentDate).toISOString() : new Date().toISOString(),
                    investmentAmount: parseFloat(investmentDetails.investmentAmount),
                    currency: investmentDetails.currency || 'INR',
                    investmentStage: investmentDetails.investmentStage || 'SEED',
                    investmentType: investmentDetails.investmentType || 'EQUITY',
                    autoGrantAccess: true,
                    accessLevel: 'FULL'
                };

                // Add optional fields if provided
                if (investmentDetails.equityPercentage) {
                    investmentData.equityPercentage = parseFloat(investmentDetails.equityPercentage);
                }
                // Entry Options
                if (investmentDetails.entrySharePrice) {
                    investmentData.entrySharePrice = parseFloat(investmentDetails.entrySharePrice);
                }
                if (investmentDetails.entryShareEquity) {
                    investmentData.entryShareEquity = parseFloat(investmentDetails.entryShareEquity);
                }
                // Exit Options
                if (investmentDetails.exitDate) {
                    investmentData.exitDate = new Date(investmentDetails.exitDate).toISOString();
                }
                if (investmentDetails.exitValuation) {
                    investmentData.exitValuation = parseFloat(investmentDetails.exitValuation);
                }
                if (investmentDetails.notes) {
                    investmentData.notes = investmentDetails.notes;
                }

                return fetch(getApiUrl('investments'), {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(investmentData)
                });
            });

            const results = await Promise.allSettled(investmentPromises);
            const successful = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
            const failed = results.length - successful;

            if (successful > 0) {
                setAlert({
                    isOpen: true,
                    title: 'Success',
                    message: `Successfully linked startup with ${successful} investor(s)${failed > 0 ? `. ${failed} failed.` : ''}`,
                    type: 'success'
                });
                setShowLinkStartupModal(false);
                setSelectedStartupId('');
                setSelectedInvestorIds([]);
                setInvestmentDetails({
                    investmentAmount: '',
                    currency: 'INR',
                    investmentDate: new Date().toISOString().split('T')[0],
                    equityPercentage: '',
                    entrySharePrice: '',
                    entryShareEquity: '',
                    exitDate: '',
                    exitValuation: '',
                    investmentStage: 'SEED',
                    investmentType: 'EQUITY',
                    notes: ''
                });
                // Reload related startups and investments to show the newly linked one
                await loadRelatedStartups(investors);
                await loadInvestments(investors);
            } else {
                setAlert({
                    isOpen: true,
                    title: 'Error',
                    message: 'Failed to link startup with investors. They may already be linked.',
                    type: 'error'
                });
            }
        } catch (error) {
            console.error('Failed to link startup:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to link startup with investors', type: 'error' });
        } finally {
            setLinkLoading(false);
        }
    };

    const handleInvestorToggle = (investorId) => {
        setSelectedInvestorIds(prev => {
            if (prev.includes(investorId)) {
                return prev.filter(id => id !== investorId);
            } else {
                return [...prev, investorId];
            }
        });
    };

    const openEditModal = () => {
        setEditData({
            organizationName: network.organizationName || network.name || '',
            organizationType: network.organizationType || '',
            email: network.email || '',
            phoneNumber: network.phoneNumber || '',
            department: network.department || ''
        });
        setShowEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(editData)
            });

            if (response.ok) {
                setShowEditModal(false);
                loadNetworkDetails();
                setAlert({ isOpen: true, title: 'Success', message: 'Network updated successfully!', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to update network', type: 'error' });
            }
        } catch (error) {
            console.error('Failed to update network:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to update network', type: 'error' });
        }
    };

    const handleDelete = async () => {
        try {
            await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            navigate(backUrl);
        } catch (error) {
            console.error('Failed to delete network:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!network) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900">Network not found</h2>
                    <button onClick={() => navigate(backUrl)} className="mt-4 text-blue-600 hover:text-blue-800">
                        ← {isNetworkUser ? 'Back to Dashboard' : 'Back to Investor Networks'}
                    </button>
                </div>
            </div>
        );
    }

    const detailContent = (
        <>
            {/* Page Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate(backUrl)}
                                className="text-gray-600 hover:text-gray-900"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">{network.organizationName || network.name}</h1>
                                <p className="text-sm text-gray-500 mt-1">{network.organizationType || 'Investor Network'}</p>
                            </div>
                        </div>
                        <div className="flex space-x-3">
                            {canManageNetwork && (
                                <>
                                    <button
                                        onClick={openEditModal}
                                        className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition"
                                        title="Edit Network"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition"
                                        title="Delete Network"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Network Details Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow p-4 space-y-4">
                            <h2 className="text-lg font-semibold text-gray-900">Network Details</h2>

                            <div>
                                <label className="text-sm font-medium text-gray-500">Email</label>
                                <p className="mt-1 text-gray-900">{network.email}</p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-500">Phone</label>
                                <p className="mt-1 text-gray-900">{network.phoneNumber || 'N/A'}</p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-500">Organization Type</label>
                                <p className="mt-1 text-gray-900">{network.organizationType || 'N/A'}</p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-500">Investment Focus</label>
                                <p className="mt-1 text-gray-900">{network.department || 'N/A'}</p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-500">Status</label>
                                <p className="mt-1">
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                        Active
                                    </span>
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-gray-500">Total Investors</label>
                                <p className="mt-1 text-2xl font-bold text-gray-900">{investors.length}</p>
                            </div>
                        </div>
                    </div>

                    {/* Tabs Content */}
                    <div className="lg:col-span-2">
                        {/* Tabs */}
                        <div className="bg-white rounded-lg shadow mb-6">
                            <div className="border-b border-gray-200">
                                <nav className="flex -mb-px">
                                    {(isInvestorViewer
                                        ? [
                                            { id: 'investors', label: 'Members' },
                                            { id: 'startups', label: 'Invested Companies' }
                                        ]
                                        : [
                                            { id: 'investors', label: 'Investors' },
                                            { id: 'users', label: 'Users' },
                                            { id: 'startups', label: 'Startups' },
                                            { id: 'investment-tranches', label: 'Investment Tranches' },
                                            { id: 'events', label: 'Events' },
                                            { id: 'files', label: 'Files' }
                                        ]).map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => {
                                                setActiveTab(tab.id);
                                                setSearchParams({ tab: tab.id });
                                            }}
                                            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                                ? 'border-blue-600 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="bg-white rounded-lg shadow">
                            {/* Investors Tab */}
                            {activeTab === 'investors' && (
                                <div>
                                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">Individual Investors</h2>
                                            <p className="text-sm text-gray-500 mt-1">Investors associated with this network</p>
                                        </div>
                                        {canManageNetwork && (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => setShowAddExistingInvestorModal(true)}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    <span>Add Existing Investor</span>
                                                </button>
                                                <button
                                                    onClick={() => setShowAddInvestorModal(true)}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    <span>Add New Investor</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {investors.length === 0 ? (
                                        <div className="p-12 text-center">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No investors yet</h3>
                                            <p className="mt-1 text-sm text-gray-500">Add investors to this network from the main page.</p>
                                        </div>
                                    ) : (
                                        <div className="overflow-hidden">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {investors.map((investor) => (
                                                        <tr key={investor.id} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
                                                                        <span className="text-white font-medium">{investor.name?.charAt(0)?.toUpperCase()}</span>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900">{investor.name}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{investor.email}</td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{investor.phoneNumber || 'N/A'}</td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                                    Active
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Users Tab */}
                            {!isInvestorViewer && activeTab === 'users' && (
                                <div>
                                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">Network Users</h2>
                                            <p className="text-sm text-gray-500 mt-1">Manage users with roles and permissions in this network</p>
                                        </div>
                                        {canManageNetwork && (
                                            <button
                                                onClick={() => {
                                                    setSelectedNetworkUser(null);
                                                    setIsEditingNetworkUser(false);
                                                    setShowNetworkUserModal(true);
                                                }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                </svg>
                                                <span>Add User</span>
                                            </button>
                                        )}
                                    </div>

                                    {networkUsersLoading ? (
                                        <div className="p-12 text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                            <p className="mt-2 text-sm text-gray-500">Loading users...</p>
                                        </div>
                                    ) : networkUsers.length === 0 ? (
                                        <div className="p-12 text-center">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No users configured</h3>
                                            <p className="mt-1 text-sm text-gray-500">Add users to this network with specific roles and permissions.</p>
                                            {canManageNetwork && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedNetworkUser(null);
                                                        setIsEditingNetworkUser(false);
                                                        setShowNetworkUserModal(true);
                                                    }}
                                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                                >
                                                    Add First User
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {networkUsers.map((user) => (
                                                        <tr key={user.id} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                                                                        <span className="text-white font-medium">{user.name?.charAt(0)?.toUpperCase()}</span>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                                        <div className="text-sm text-gray-500">{user.email}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.networkRole)}`}>
                                                                    {user.networkRole || 'MANAGEMENT'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {user.canViewFiles && (
                                                                        <span className="inline-flex px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                                                                            View Files
                                                                        </span>
                                                                    )}
                                                                    {user.canViewInvestments && (
                                                                        <span className="inline-flex px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded">
                                                                            View Investments
                                                                        </span>
                                                                    )}
                                                                    {user.canManageUsers && (
                                                                        <span className="inline-flex px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                                                                            Manage Users
                                                                        </span>
                                                                    )}
                                                                    {user.canManageFiles && (
                                                                        <span className="inline-flex px-2 py-0.5 text-xs bg-orange-50 text-orange-700 rounded">
                                                                            Manage Files
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {user.isActive !== false ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                                                <div className="flex items-center space-x-2">
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedNetworkUser(user);
                                                                            setIsEditingNetworkUser(true);
                                                                            setShowNetworkUserModal(true);
                                                                        }}
                                                                        className="text-blue-600 hover:text-blue-900"
                                                                        title="Edit User"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                        </svg>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            setUserToRemove(user);
                                                                            setShowRemoveUserModal(true);
                                                                        }}
                                                                        className="text-red-600 hover:text-red-900"
                                                                        title="Remove User"
                                                                    >
                                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Startups Tab */}
                            {activeTab === 'startups' && (
                                <div>
                                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">Related Startups</h2>
                                            <p className="text-sm text-gray-500 mt-1">Startups where investors from this network have invested</p>
                                        </div>
                                        {canManageNetwork && (
                                            <button
                                                onClick={() => {
                                                    setShowLinkStartupModal(true);
                                                    setSelectedStartupId('');
                                                    setSelectedInvestorIds([]);
                                                    setInvestmentDetails({
                                                        investmentAmount: '',
                                                        currency: 'INR',
                                                        investmentDate: new Date().toISOString().split('T')[0],
                                                        equityPercentage: '',
                                                        entrySharePrice: '',
                                                        entryShareEquity: '',
                                                        investmentStage: 'SEED',
                                                        investmentType: 'EQUITY',
                                                        notes: ''
                                                    });
                                                }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                </svg>
                                                <span>Link Startup</span>
                                            </button>
                                        )}
                                    </div>

                                    {investments.length === 0 ? (
                                        <div className="p-12 text-center">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No investments yet</h3>
                                            <p className="mt-1 text-sm text-gray-500">Investments will appear here once network investors make investments.</p>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Startup</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investor</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Share Price</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {investments.map((investment) => (
                                                        <tr
                                                            key={investment.id}
                                                            onClick={() => {
                                                                setSelectedInvestment(investment);
                                                                setShowInvestmentDetailModal(true);
                                                            }}
                                                            className="hover:bg-gray-50 cursor-pointer"
                                                        >
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center mr-2">
                                                                        <span className="text-white text-xs font-medium">
                                                                            {(investment.startupName || 'N')?.charAt(0)?.toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-medium text-gray-900">
                                                                            {investment.startupName || 'N/A'}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">{investment.startupEmail || ''}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <p className="text-sm text-gray-900">{investment.investorName || 'N/A'}</p>
                                                                <p className="text-xs text-gray-500">{investment.investorEmail || ''}</p>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <p className="text-sm text-gray-900">
                                                                    {investment.currency || 'INR'} {investment.investmentAmount?.toLocaleString() || '0'}
                                                                </p>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <p className="text-sm text-gray-900">
                                                                    {investment.entrySharePrice ? `${investment.currency || 'INR'} ${investment.entrySharePrice.toLocaleString()}` : '-'}
                                                                </p>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <p className="text-sm text-gray-900">
                                                                    {investment.investmentDate ? new Date(investment.investmentDate).toLocaleDateString() : '-'}
                                                                </p>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${investment.status === 'ACTIVE'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : investment.status === 'EXITED'
                                                                        ? 'bg-gray-100 text-gray-800'
                                                                        : 'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                    {investment.status || 'ACTIVE'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                                                                {investment.status === 'ACTIVE' && !isInvestorViewer && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedInvestment(investment);
                                                                            setExitDetails({
                                                                                exitDate: new Date().toISOString().split('T')[0],
                                                                                exitAmount: investment.exitAmount?.toString() || '',
                                                                                exitValuation: investment.exitValuation?.toString() || ''
                                                                            });
                                                                            setShowExitModal(true);
                                                                        }}
                                                                        className="text-red-600 hover:text-red-900"
                                                                    >
                                                                        Exit
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Events Tab */}
                            {!isInvestorViewer && activeTab === 'events' && (
                                <div>
                                    <div className="px-4 py-3 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900">Related Events</h2>
                                        <p className="text-sm text-gray-500 mt-1">Pitch events where network investors are participants or organizers</p>
                                    </div>

                                    {events.length === 0 ? (
                                        <div className="p-12 text-center">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
                                            <p className="mt-1 text-sm text-gray-500">Events will appear here when network investors participate in pitch events.</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-200">
                                            {events.map((event) => (
                                                <div key={event.id} className="px-4 py-3 hover:bg-gray-50 transition">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
                                                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{event.eventName}</p>
                                                                <p className="text-xs text-gray-500">
                                                                    {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : 'Date TBD'} • {event.location || 'Location TBD'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${event.status === 'UPCOMING' ? 'bg-blue-100 text-blue-800' :
                                                                event.status === 'ONGOING' ? 'bg-green-100 text-green-800' :
                                                                    event.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
                                                                        'bg-red-100 text-red-800'
                                                                }`}>
                                                                {event.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Files Tab */}
                            {!isInvestorViewer && activeTab === 'files' && (
                                <div>
                                    <div className="px-4 py-3 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900">Related Files</h2>
                                        <p className="text-sm text-gray-500 mt-1">Files associated with this investor network</p>
                                    </div>

                                    {files.length === 0 ? (
                                        <div className="p-12 text-center">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No files yet</h3>
                                            <p className="mt-1 text-sm text-gray-500">Files associated with this network will appear here.</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-200">
                                            {files.map((file) => (
                                                <div key={file.id} className="px-4 py-3 hover:bg-gray-50 transition">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                                                                <p className="text-xs text-gray-500">
                                                                    {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : 'N/A'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Investment Tranches Tab */}
                            {!isInvestorViewer && activeTab === 'investment-tranches' && (
                                <div>
                                    <div className="px-4 py-3 border-b border-gray-200">
                                        <h2 className="text-lg font-semibold text-gray-900">Investment Tranches</h2>
                                        <p className="text-sm text-gray-500 mt-1">Tranches where this network has committed investments</p>
                                    </div>
                                    <div className="p-4">
                                        {networkRounds.length === 0 ? (
                                            <div className="text-center py-12">
                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <h3 className="mt-2 text-sm font-medium text-gray-900">No investment tranches</h3>
                                                <p className="mt-1 text-sm text-gray-500">This network hasn't committed to any funding tranches yet.</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                {/* Tranches List */}
                                                <div className="space-y-3">
                                                    {networkRounds.map(round => (
                                                        <div
                                                            key={round.id}
                                                            onClick={() => setSelectedNetworkRound(round)}
                                                            className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${selectedNetworkRound?.id === round.id ? 'bg-blue-50 border-blue-300' : ''
                                                                }`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <h4 className="font-semibold text-gray-900">{round.roundName}</h4>
                                                                    <p className="text-sm text-gray-500">{round.startupName}</p>
                                                                </div>
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoundStatusBadge(round.status)}`}>
                                                                    {round.status}
                                                                </span>
                                                            </div>
                                                            <div className="mt-2 flex items-center justify-between text-sm">
                                                                <span className="text-gray-600">
                                                                    Committed: {formatCurrency(round.committedAmount)}
                                                                </span>
                                                                <span className="text-green-600">
                                                                    Disbursed: {formatCurrency(round.disbursedAmount)}
                                                                </span>
                                                            </div>
                                                            {round.committedAmount > 0 && (
                                                                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="bg-green-600 h-2 rounded-full"
                                                                        style={{ width: `${Math.min((round.disbursedAmount / round.committedAmount) * 100 || 0, 100)}%` }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Tranche Details */}
                                                <div>
                                                    {selectedNetworkRound ? (
                                                        <div className="bg-gray-50 rounded-lg border p-4">
                                                            <h3 className="text-lg font-bold text-gray-900 mb-4">
                                                                {selectedNetworkRound.roundName} - {selectedNetworkRound.startupName}
                                                            </h3>
                                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                                <div>
                                                                    <p className="text-gray-500">Committed Amount</p>
                                                                    <p className="font-semibold">{formatCurrency(selectedNetworkRound.committedAmount)}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-gray-500">Disbursed Amount</p>
                                                                    <p className="font-semibold text-green-600">{formatCurrency(selectedNetworkRound.disbursedAmount)}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-gray-500">Network Investment</p>
                                                                    <p className="font-semibold">{formatCurrency(selectedNetworkRound.networkInvestmentAmount)}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-gray-500">Investment Type</p>
                                                                    <p className="font-semibold">{selectedNetworkRound.investmentType || '-'}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-gray-500">Equity %</p>
                                                                    <p className="font-semibold">{selectedNetworkRound.equityPercentage ? `${selectedNetworkRound.equityPercentage}%` : '-'}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-gray-500">Shares</p>
                                                                    <p className="font-semibold">{selectedNetworkRound.numberOfShares?.toLocaleString() || '-'}</p>
                                                                </div>
                                                            </div>

                                                            {/* Member Participations */}
                                                            {selectedNetworkRound.participations && selectedNetworkRound.participations.length > 0 && (
                                                                <div className="mt-4 pt-4 border-t">
                                                                    <h4 className="font-medium text-gray-700 mb-2">Member Participations</h4>
                                                                    <div className="space-y-2">
                                                                        {selectedNetworkRound.participations.map(p => (
                                                                            <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                                                                                <div>
                                                                                    <p className="font-medium">{p.investorName}</p>
                                                                                    <p className="text-gray-500 text-xs">{p.participationPercentage?.toFixed(2)}% participation</p>
                                                                                </div>
                                                                                <span className="font-semibold">{formatCurrency(p.amount)}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Payment Terms */}
                                                            {selectedNetworkRound.terms && selectedNetworkRound.terms.length > 0 && (
                                                                <div className="mt-4 pt-4 border-t">
                                                                    <h4 className="font-medium text-gray-700 mb-2">Payment Terms</h4>
                                                                    <div className="space-y-2">
                                                                        {selectedNetworkRound.terms.map(term => (
                                                                            <div key={term.id} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                                                                                <div>
                                                                                    <p className="font-medium">Term #{term.termNumber}</p>
                                                                                    <p className="text-gray-500 text-xs">Due: {term.dueDate || 'Not set'}</p>
                                                                                </div>
                                                                                <div className="flex items-center space-x-2">
                                                                                    <span className="font-semibold">{formatCurrency(term.amount)}</span>
                                                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(term.status)}`}>
                                                                                        {term.status}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="bg-gray-50 rounded-lg border p-8 text-center">
                                                            <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                        <p className="text-gray-500">Select a tranche to view details</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <CreateInvestorModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        loadNetworkDetails();
                    }}
                    initialType="NETWORK"
                    initialData={network}
                    isEdit={true}
                />
            )}

            {showCreateNetworkUserModal && (
                <CreateNetworkUserModal
                    isOpen={showCreateNetworkUserModal}
                    onClose={() => setShowCreateNetworkUserModal(false)}
                    onSuccess={() => {
                        setShowCreateNetworkUserModal(false);
                        loadNetworkDetails();
                    }}
                    networkId={id}
                />
            )}

            {showCreateNetworkUserModal && (
                <CreateNetworkUserModal
                    isOpen={showCreateNetworkUserModal}
                    onClose={() => setShowCreateNetworkUserModal(false)}
                    onSuccess={() => {
                        setShowCreateNetworkUserModal(false);
                        loadNetworkDetails();
                    }}
                    networkId={id}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Network?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete "{network.organizationName || network.name}"?
                            {investors.length > 0 && (
                                <span className="block mt-2 text-red-600 font-medium">
                                    Warning: This network has {investors.length} investor(s). They will be unlinked.
                                </span>
                            )}
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Link Startup Modal */}
            {showLinkStartupModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold text-white">Link Startup with Investors</h2>
                                <button
                                    onClick={() => {
                                        setShowLinkStartupModal(false);
                                        setSelectedStartupId('');
                                        setSelectedInvestorIds([]);
                                        setInvestmentDetails({
                                            investmentAmount: '',
                                            currency: 'INR',
                                            investmentDate: new Date().toISOString().split('T')[0],
                                            equityPercentage: '',
                                            entrySharePrice: '',
                                            entryShareEquity: '',
                                            investmentStage: 'SEED',
                                            investmentType: 'EQUITY',
                                            notes: ''
                                        });
                                    }}
                                    className="text-white hover:text-gray-200 transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Startup Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Startup <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedStartupId}
                                    onChange={(e) => setSelectedStartupId(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">-- Select a Startup --</option>
                                    {allStartups
                                        .filter(startup => !startups.some(s => s.id === startup.id))
                                        .map((startup) => (
                                            <option key={startup.id} value={startup.id}>
                                                {startup.organizationName || startup.name} ({startup.email})
                                            </option>
                                        ))}
                                </select>
                                {allStartups.filter(startup => !startups.some(s => s.id === startup.id)).length === 0 && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        All available startups are already linked to this network.
                                    </p>
                                )}
                            </div>

                            {/* Investor Selection */}
                            {investors.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Investors from Network <span className="text-red-500">*</span>
                                    </label>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Select one or more investors from this network to link with the startup
                                    </p>
                                    <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                                        {investors.map((investor) => (
                                            <label
                                                key={investor.id}
                                                className="flex items-center px-4 py-3 hover:bg-gray-50 border-b border-gray-200 last:border-b-0 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedInvestorIds.includes(investor.id)}
                                                    onChange={() => handleInvestorToggle(investor.id)}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <div className="ml-3 flex-1">
                                                    <div className="flex items-center">
                                                        <div className="h-8 w-8 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center mr-3">
                                                            <span className="text-white text-xs font-medium">
                                                                {investor.name?.charAt(0)?.toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{investor.name}</p>
                                                            <p className="text-xs text-gray-500">{investor.email}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        {selectedInvestorIds.length} investor(s) selected
                                    </p>
                                </div>
                            )}

                            {investors.length === 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <p className="text-sm text-yellow-800">
                                        No investors in this network. Please add investors first before linking startups.
                                    </p>
                                </div>
                            )}

                            {/* Investment Details Section */}
                            {selectedStartupId && selectedInvestorIds.length > 0 && (
                                <div className="border-t pt-4 mt-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Details</h3>
                                    <p className="text-xs text-gray-500 mb-4">
                                        Enter investment information that will apply to all selected investors
                                    </p>

                                    {/* Basic Investment Info */}
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Amount <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={investmentDetails.investmentAmount}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentAmount: e.target.value })}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="1000000"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Currency <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={investmentDetails.currency}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, currency: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="INR">INR (₹)</option>
                                                <option value="USD">USD ($)</option>
                                                <option value="EUR">EUR (€)</option>
                                                <option value="GBP">GBP (£)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={investmentDetails.investmentDate}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentDate: e.target.value })}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Stage <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={investmentDetails.investmentStage}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentStage: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="SEED">Seed</option>
                                                <option value="SERIES_A">Series A</option>
                                                <option value="SERIES_B">Series B</option>
                                                <option value="SERIES_C">Series C</option>
                                                <option value="GROWTH">Growth</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Investment Type <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={investmentDetails.investmentType}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, investmentType: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="EQUITY">Equity</option>
                                                <option value="CONVERTIBLE_NOTE">Convertible Note</option>
                                                <option value="SAFE">SAFE</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Equity Percentage (%)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={investmentDetails.equityPercentage}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, equityPercentage: e.target.value })}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="10.5"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Entry Share Price
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={investmentDetails.entrySharePrice}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, entrySharePrice: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="100.00"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Price per share at entry</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Entry Share Equity
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={investmentDetails.entryShareEquity}
                                                onChange={(e) => setInvestmentDetails({ ...investmentDetails, entryShareEquity: e.target.value })}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="10000"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Total equity/shares acquired</p>
                                        </div>
                                    </div>

                                    {/* Entry Options Section */}
                                    <div className="mt-6 pt-4 border-t">
                                        <h4 className="text-md font-semibold text-gray-900 mb-4">Entry Options</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Entry Share Price
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={investmentDetails.entrySharePrice}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, entrySharePrice: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="100.00"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Price per share at entry</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Entry Share Equity
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={investmentDetails.entryShareEquity}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, entryShareEquity: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="10000"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Total equity/shares acquired</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Exit Options Section */}
                                    <div className="mt-6 pt-4 border-t">
                                        <h4 className="text-md font-semibold text-gray-900 mb-4">Exit Options</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Exit Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={investmentDetails.exitDate}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, exitDate: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Leave empty if investment is still active</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Exit Valuation
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={investmentDetails.exitValuation}
                                                    onChange={(e) => setInvestmentDetails({ ...investmentDetails, exitValuation: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder="5000000"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Company valuation at exit</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Notes
                                        </label>
                                        <textarea
                                            value={investmentDetails.notes}
                                            onChange={(e) => setInvestmentDetails({ ...investmentDetails, notes: e.target.value })}
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Additional notes about this investment..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    onClick={() => {
                                        setShowLinkStartupModal(false);
                                        setSelectedStartupId('');
                                        setSelectedInvestorIds([]);
                                        setInvestmentDetails({
                                            investmentAmount: '',
                                            currency: 'INR',
                                            investmentDate: new Date().toISOString().split('T')[0],
                                            equityPercentage: '',
                                            entrySharePrice: '',
                                            entryShareEquity: '',
                                            investmentStage: 'SEED',
                                            investmentType: 'EQUITY',
                                            notes: ''
                                        });
                                    }}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleLinkStartup}
                                    disabled={linkLoading || !selectedStartupId || selectedInvestorIds.length === 0 || !investmentDetails.investmentAmount}
                                    className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                >
                                    {linkLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>Linking...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                            </svg>
                                            <span>Link Startup</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Investment Detail Modal */}
            {showInvestmentDetailModal && selectedInvestment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold text-white">Investment Details</h2>
                                <button
                                    onClick={() => {
                                        setShowInvestmentDetailModal(false);
                                        setSelectedInvestment(null);
                                    }}
                                    className="text-white hover:text-gray-200 transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            <InvestmentTabbedDetails investment={selectedInvestment} formatMoney={formatMoney} formatDateTime={formatDateTime} />

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setShowInvestmentDetailModal(false);
                                        setSelectedInvestment(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Close
                                </button>
                                {selectedInvestment.status === 'ACTIVE' && !isInvestorViewer && (
                                    <button
                                        onClick={() => {
                                            setShowInvestmentDetailModal(false);
                                            setExitDetails({
                                                exitDate: new Date().toISOString().split('T')[0],
                                                exitAmount: selectedInvestment.exitAmount?.toString() || '',
                                                exitValuation: selectedInvestment.exitValuation?.toString() || ''
                                            });
                                            setShowExitModal(true);
                                        }}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                    >
                                        Mark as Exited
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Exit Investment Modal */}
            {showExitModal && selectedInvestment && !isInvestorViewer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full">
                        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold text-white">Mark Investment as Exited</h2>
                                <button
                                    onClick={() => {
                                        setShowExitModal(false);
                                        setSelectedInvestment(null);
                                        setExitDetails({
                                            exitDate: new Date().toISOString().split('T')[0],
                                            exitAmount: '',
                                            exitValuation: ''
                                        });
                                    }}
                                    className="text-white hover:text-gray-200 transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-blue-900">
                                    <strong>Startup:</strong> {selectedInvestment.startupName || 'N/A'}
                                </p>
                                <p className="text-sm text-blue-900 mt-1">
                                    <strong>Investor:</strong> {selectedInvestment.investorName || 'N/A'}
                                </p>
                                <p className="text-sm text-blue-900 mt-1">
                                    <strong>Investment Amount:</strong> {selectedInvestment.currency || 'INR'} {selectedInvestment.investmentAmount?.toLocaleString() || '0'}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Exit Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={exitDetails.exitDate}
                                    onChange={(e) => setExitDetails({ ...exitDetails, exitDate: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Exit Amount
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={exitDetails.exitAmount}
                                    onChange={(e) => setExitDetails({ ...exitDetails, exitAmount: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Amount received on exit"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Exit Valuation
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={exitDetails.exitValuation}
                                    onChange={(e) => setExitDetails({ ...exitDetails, exitValuation: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Company valuation at exit"
                                />
                                <p className="text-xs text-gray-500 mt-1">Company valuation at exit</p>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    onClick={() => {
                                        setShowExitModal(false);
                                        setSelectedInvestment(null);
                                        setExitDetails({
                                            exitDate: new Date().toISOString().split('T')[0],
                                            exitAmount: '',
                                            exitValuation: ''
                                        });
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleExitInvestment}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Mark as Exited
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Investor Modal */}
            <CreateInvestorModal
                isOpen={showAddInvestorModal}
                onClose={() => setShowAddInvestorModal(false)}
                onSuccess={() => {
                    setShowAddInvestorModal(false);
                    loadNetworkDetails(); // Reload to show the new investor
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: 'Investor added successfully!',
                        type: 'success'
                    });
                }}
                initialType="INDIVIDUAL"
                initialData={{ investorNetworkId: id }}
                disableNetworkType={true}
            />

            {/* Add Existing Investor Modal */}
            {showAddExistingInvestorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-semibold text-gray-900">Add Existing Investor</h3>
                            <button
                                onClick={() => {
                                    setShowAddExistingInvestorModal(false);
                                    setSelectedExistingInvestorId('');
                                    setSearchTerm('');
                                }}
                                className="text-gray-400 hover:text-gray-500 transition"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex flex-col md:flex-row h-[500px]">
                            {/* Left Side: Search & Instructions */}
                            <div className="w-full md:w-1/3 bg-gray-50 p-6 border-r border-gray-200 flex flex-col">
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Search Investor
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search by name or email..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Search for an investor to add to this network.
                                    </p>
                                </div>

                                <div className="mt-auto">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-blue-900 mb-1">Selected Investors</h4>
                                        {selectedExistingInvestorIds.length > 0 ? (
                                            <div>
                                                <p className="text-sm font-bold text-blue-800">
                                                    {selectedExistingInvestorIds.length} selected
                                                </p>
                                                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                                                    {selectedExistingInvestorIds.map(id => {
                                                        const inv = allInvestors.find(i => i.id.toString() === id.toString());
                                                        return inv ? (
                                                            <p key={id} className="text-xs text-blue-600 truncate flex items-center">
                                                                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-1.5"></span>
                                                                {inv.name}
                                                            </p>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-blue-500 italic">None selected</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Results List */}
                            <div className="w-full md:w-2/3 p-6 overflow-y-auto bg-white">
                                <h4 className="text-sm font-medium text-gray-700 mb-3 uppercase tracking-wider">Available Investors</h4>

                                {allInvestors
                                    .filter(inv => !investors.some(existing => existing.id === inv.id))
                                    .filter(inv =>
                                        inv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        inv.email.toLowerCase().includes(searchTerm.toLowerCase())
                                    ).length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                        <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        <p>No matching investors found</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-3">
                                        {allInvestors
                                            .filter(inv => !investors.some(existing => existing.id === inv.id))
                                            .filter(inv =>
                                                inv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                inv.email.toLowerCase().includes(searchTerm.toLowerCase())
                                            )
                                            .map((investor) => {
                                                const isSelected = selectedExistingInvestorIds.includes(investor.id);
                                                return (
                                                    <div
                                                        key={investor.id}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                setSelectedExistingInvestorIds(prev => prev.filter(id => id !== investor.id));
                                                            } else {
                                                                setSelectedExistingInvestorIds(prev => [...prev, investor.id]);
                                                            }
                                                        }}
                                                        className={`p-3 rounded-lg border-2 cursor-pointer transition flex items-center justify-between ${isSelected
                                                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                                            : 'border-gray-100 hover:border-blue-200 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <div className="flex items-center space-x-3 overflow-hidden">
                                                            {/* Checkbox for visual clarity */}
                                                            <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected
                                                                ? 'bg-blue-600 border-blue-600'
                                                                : 'bg-white border-gray-300'
                                                                }`}>
                                                                {isSelected && (
                                                                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                )}
                                                            </div>

                                                            <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-200 text-blue-700' : 'bg-gray-100 text-gray-500'
                                                                }`}>
                                                                <span className="font-medium text-sm">{investor.name?.charAt(0)?.toUpperCase()}</span>
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                                                    {investor.name}
                                                                </p>
                                                                <p className={`text-xs truncate ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
                                                                    {investor.email}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowAddExistingInvestorModal(false);
                                    setSelectedExistingInvestorIds([]);
                                    setSearchTerm('');
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddExistingInvestor}
                                disabled={addExistingLoading || selectedExistingInvestorIds.length === 0}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm transition transform active:scale-95"
                            >
                                {addExistingLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Adding...
                                    </>
                                ) : (
                                    'Add to Network'
                                )}
                            </button>
                        </div>
                    </div>
                </div >
            )}

            {/* Network User Modal */}
            <NetworkUserModal
                isOpen={showNetworkUserModal}
                onClose={() => {
                    setShowNetworkUserModal(false);
                    setSelectedNetworkUser(null);
                    setIsEditingNetworkUser(false);
                }}
                onSuccess={() => {
                    loadNetworkUsers();
                    setAlert({
                        isOpen: true,
                        title: 'Success',
                        message: isEditingNetworkUser ? 'User updated successfully!' : 'User added successfully!',
                        type: 'success'
                    });
                }}
                networkId={id}
                user={selectedNetworkUser}
                isEdit={isEditingNetworkUser}
            />

            {/* Remove User Confirmation Modal */}
            {showRemoveUserModal && userToRemove && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove User from Network?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to remove <strong>{userToRemove.name}</strong> from this network?
                            They will lose all their permissions associated with this network.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowRemoveUserModal(false);
                                    setUserToRemove(null);
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemoveNetworkUser}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Remove User
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
            />
        </>
    );

    return detailContent;
};

export default InvestorNetworkDetail;
